import React from 'react';
import {
  AuthDataContext,
  STEP_PASSWORD,
  STEP_TOTP_CONFIG,
  STEP_TOTP_SN_WARNING,
  STEP_USERNAME,
} from './context';
import CoreClient from '@c42/core-client';
import C42Jwt from '@c42/jwt';
import { useHistory } from 'react-router';
import { DEFAULT_REDIRECT_AFTER_LOGIN, LOGIN_INDEX, SSO_INDEX } from '../../../constants/routes';
import { useRoutingData } from '../routing/hooks';
import { NAV_EXTERNALS } from '../../../utils/nav';
import { MATCH_ALL_BACKSLASHES } from '../../../constants/regexes';
import { TOTP_CONFIG_REQUIRED } from '../../../constants/errors';
import Text from '../../../../shared/Text';
import { POST, request } from '../../../utils/request';

export const c42Jwt = new C42Jwt();

export const CONSOLE_LOGIN_PERMISSIONS = ['admin.console.login', 'console.login'];
export const CONSOLE_LOGIN_PERMISSIONS_ERROR = 'NoConsoleLoginPermission';

export const API_AUTHORITY_LOGIN_CONFIG_PATH = '/api/v3/LoginConfiguration';
export const API_TOTP_CREATE_SECRET_KEY_PATH = '/api/v9/totp-auth-factor/create-secret-key';
export const API_TOTP_COMPLETE_CONFIG_PATH = '/api/v9/totp-auth-factor/complete-configuration';

export const useAuthData = () => React.useContext(AuthDataContext).authData;

export const useSetAuthData = () => React.useContext(AuthDataContext).setAuthData;

export const useGetServerEnv = () => {
  const setAuthData = useSetAuthData();

  return async () => {
    const serverEnv = await CoreClient.ServerEnv.find();
    c42Jwt.register(serverEnv);
    const { clusterType, serverType } = serverEnv;

    setAuthData((current) => ({
      ...current,
      clusterType,
      serverType,
    }));
    return { serverEnv };
  };
};

export const useAuthenticate = () => {
  const setAuthData = useSetAuthData();

  return async (opts?: any) => {
    setAuthData((current) => ({
      ...current,
      isAuthenticating: true,
      errorAuthenticating: null,
      loginError: null,
    }));

    try {
      if (!opts) {
        await c42Jwt.verifyAuthenticated();
      } else if (opts.loginToken) {
        // If there's already an OAuth token in localstorage, clear it and login again.
        await c42Jwt
          .verifyAuthenticated()
          .then(() => c42Jwt.logout())
          .catch(() => Promise.resolve()) // Swallow expected exception when we are unauthenticated
          .then(() => c42Jwt.loginWithLoginToken(opts.loginToken));
      } else if (opts.isLogin && opts.username && opts.password) {
        await c42Jwt.login(opts.username, opts.password, opts.totp || '');
      } else {
        throw 'Unsupported scenario encountered, opts: ' + JSON.stringify(opts);
      }
    } catch (e) {
      setAuthData((current) => ({
        ...current,
        isAuthenticating: false,
        errorAuthenticating: null,
        loginError: null,
      }));
      throw e;
    }
  };
};

export const useDeauthenticate = () => {
  const history = useHistory();
  const setAuthData = useSetAuthData();

  return async () => {
    setAuthData((current) => ({
      ...current,
      isDeauthenticating: true,
    }));
    try {
      await c42Jwt.logout();
    } finally {
      setAuthData((current) => ({
        ...current,
        isDeauthenticating: false,
      }));
      history.push(LOGIN_INDEX);
    }
  };
};

export const useOnSubmitLoginSuccess = () => {
  const setAuthData = useSetAuthData();

  return (redirectParam: string) => {
    let redirectPath: string;
    setAuthData((current) => ({
      ...current,
      isAuthenticating: false,
    }));

    if (redirectParam) {
      /**
       * Plus signs change their meaning when encoded, decoded, and then parsed as a URL param.
       * Since the redirectParam is a URI encoded path that may include its own params, we
       * need to re-encode the plus signs so they don't get converted into spaces.
       * React Router already decoded the to query param when we initially set it in our state
       * So we don't need to decode yet again here
       */
      redirectPath = redirectParam.replace(/\+/g, '%2B');

      // verify the redirect path starts with a "/"
      // therefore it may not a be javascript action or something else encoded
      if (!redirectPath.startsWith('/') && !decodeURIComponent(redirectPath).startsWith('/')) {
        redirectPath = DEFAULT_REDIRECT_AFTER_LOGIN;
      }
    } else {
      redirectPath = DEFAULT_REDIRECT_AFTER_LOGIN;
    }
    // Remove any potential backslashes from the redirectPath (PL-105140)
    redirectPath = redirectPath.replace(MATCH_ALL_BACKSLASHES, '');

    NAV_EXTERNALS.cypressLocationReplace(redirectPath);
  };
};

export const useOnSubmitLoginError = () => {
  const getServerEnv = useGetServerEnv();
  const setAuthData = useSetAuthData();
  const fetchTotpSecret = useFetchTotpSecret();

  return async (err: JQueryXHR) => {
    c42Jwt.logout();

    // ServerEnv is reloaded on auth error to reload challenge from server.
    const { serverEnv } = await getServerEnv();
    const isStorageNode = serverEnv?.serverType?.storage;
    switch (err.toString()) {
      case TOTP_CONFIG_REQUIRED:
        if (isStorageNode) {
          setAuthData((current) => ({
            ...current,
            currentStep: STEP_TOTP_SN_WARNING,
          }));
        } else {
          await fetchTotpSecret();
          setAuthData((current) => ({
            ...current,
            currentStep: STEP_TOTP_CONFIG,
          }));
        }
        return;

      case CONSOLE_LOGIN_PERMISSIONS_ERROR:
        setAuthData((current) => ({
          ...current,
          isAuthenticating: false,
          errorAuthenticating: Text.get('login_error_auth'),
          loginError: Text.get('login_error_auth'),
        }));
        return;

      default:
        setAuthData((current) => ({
          ...current,
          isAuthenticating: false,
          errorAuthenticating: Text.get('login_error_incorrect'),
          loginError: Text.get('login_error_incorrect'),
        }));
        return;
    }
  };
};

export const useResetForm = () => {
  const history = useHistory();
  const setAuthData = useSetAuthData();

  return () => {
    history.push(LOGIN_INDEX);
    setAuthData((current) => ({
      ...current,
      currentStep: STEP_USERNAME,
    }));
  };
};

export const useSubmitUsername = () => {
  const history = useHistory();
  const { username } = useAuthData();
  const setAuthData = useSetAuthData();

  return async () => {
    setAuthData((current) => ({
      ...current,
      isFetchingLoginConfig: true,
      errorFetchingLoginConfig: null,
    }));

    const {
      location: { protocol, host },
    } = window;
    const SERVER_ADDRESS = `${protocol}//${host}`;

    const params = {
      query: {
        url: SERVER_ADDRESS,
        username: username,
      },
      options: {
        url: `${API_AUTHORITY_LOGIN_CONFIG_PATH}`,
      },
    };

    return CoreClient.LoginConfiguration.find(params)
      .then((responseConfiguration: { loginType: string; regKey: string }) => {
        const { loginType, regKey } = responseConfiguration;

        const type = String(loginType).toLowerCase();

        // These login type strings are derived from Core:
        // https://stash.corp.code42.com/projects/SVR/repos/core/browse/pro_core/src/impl/java/com/code42/login/LoginType.java
        const ssoTypes = ['private_sso', 'cloud_sso', 'saml_sso'];
        const isSSO = ssoTypes.indexOf(type) > -1;
        const isUsingTotp = type === 'local_2fa';

        setAuthData((current) => ({
          ...current,
          isFetchingLoginConfig: false,
          isSSO,
          isUsingTotp,
          regKey,
        }));

        if (isSSO) {
          history.push(SSO_INDEX);
        } else {
          setAuthData((current) => ({
            ...current,
            currentStep: STEP_PASSWORD,
          }));
        }
      })
      .catch((error) => {
        setAuthData((current) => ({
          ...current,
          isFetchingLoginConfig: false,
          errorFetchingLoginConfig: Text.get('login_config_error_generic'),
        }));
        Promise.reject(error);
      });
  };
};

export const useSubmitLogin = () => {
  const authenticate = useAuthenticate();
  const onSubmitLoginSuccess = useOnSubmitLoginSuccess();
  const onSubmitLoginError = useOnSubmitLoginError();
  const { username, password, totp } = useAuthData();
  const { redirectParam } = useRoutingData();

  const checkLoginPermission = () => {
    const permissions = c42Jwt.extractPermissionsFromJwt();
    if (permissions?.length > 0) {
      const hasPermission = Boolean(
        permissions.find((permission) => CONSOLE_LOGIN_PERMISSIONS.indexOf(permission) >= 0),
      );
      if (hasPermission) {
        return true;
      }
    }
    throw CONSOLE_LOGIN_PERMISSIONS_ERROR;
  };

  return () => {
    return authenticate({
      isLogin: true,
      username,
      password,
      totp,
    })
      .then(() => checkLoginPermission())
      .then(
        () => onSubmitLoginSuccess(redirectParam),
        (err) => onSubmitLoginError(err),
      );
  };
};

export const useSubmitTotpConfig = () => {
  const setAuthData = useSetAuthData();
  const submitLogin = useSubmitLogin();
  const { username, password, totp } = useAuthData();

  return () => {
    setAuthData((current) => ({
      ...current,
      isTotpInvalid: false,
      isValidatingTotp: true,
    }));

    return request(API_TOTP_COMPLETE_CONFIG_PATH, POST, {
      body: JSON.stringify({
        username,
        password,
        totp: Number(totp),
      }),
      headers: {
        'content-type': 'application/json',
      },
    })
      .then(() => {
        setAuthData((current) => ({
          ...current,
          isValidatingTotp: false,
        }));
        submitLogin();
      })
      .catch((error) => {
        setAuthData((current) => ({
          ...current,
          isTotpInvalid: true,
          isValidatingTotp: false,
        }));
        Promise.reject(error);
      });
  };
};

const useFetchTotpSecret = () => {
  const setAuthData = useSetAuthData();
  const { username, password } = useAuthData();

  return () => {
    setAuthData((current) => ({
      ...current,
      errorAuthenticating: null,
      isAuthenticating: true,
    }));

    return request(API_TOTP_CREATE_SECRET_KEY_PATH, POST, {
      body: JSON.stringify({
        username,
        password,
      }),
      headers: {
        'content-type': 'application/json',
      },
    })
      .then((response) => {
        const { body } = response;
        if (!body?.data?.secretKey) {
          return Promise.reject('NO_SECRET_KEY');
        }
        setAuthData((current) => ({
          ...current,
          isAuthenticating: false,
          totpSecret: body.data.secretKey,
        }));
      })
      .catch(() => {
        setAuthData((current) => ({
          ...current,
          errorAuthenticating: Text.get('login_error_incorrect'),
          isAuthenticating: false,
          loginError: Text.get('login_error_incorrect'),
        }));
      });
  };
};
